import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, Phone } from 'lucide-react'
import { useLanguage } from '../context/LanguageContext'

function CountUp({ end, duration = 500, suffix = "" }) {
  const [count, setCount] = useState(0)

  useEffect(() => {
    let startTime = null
    let animationFrameId

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp
      const progress = timestamp - startTime
      const percentage = Math.min(progress / duration, 1)
      
      // Easing out quad
      const easeProgress = percentage * (2 - percentage)
      
      setCount(Math.floor(easeProgress * end))

      if (progress < duration) {
        animationFrameId = requestAnimationFrame(animate)
      } else {
        setCount(end)
      }
    }

    animationFrameId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(animationFrameId)
  }, [end, duration])

  return <>{count}{suffix}</>
}

export default function Hero() {
  const { language, t, linkTo } = useLanguage()
  const navigate = useNavigate()
  const videoRef = useRef(null)
  
  // Single video URL from assets ("Video hero wecas.mp4")
  const videoUrl = "https://assets.auxodev.dk/assets/b84a6f1a-652f-4145-af52-d7d46fab18e5/ce2a418a-736a-45d7-ab95-8db155049ede.mp4"

  // Play the video on mount
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch((err) => {
        console.warn("Video playback failed:", err)
      })
    }
  }, [])

  const heroData = {
    indicator: t('hero.badge'),
    headline: language === 'da' ? "Erhvervsrengøring og byggepladsservice med fokus på kvalitet og stabilitet" : "Commercial cleaning and construction site service with focus on quality and stability",
    description: t('hero.desc'),
    primaryCta: t('hero.cta_contact'),
    secondaryCta: language === 'da' ? "Kontakt os 24/7" : "Contact us 24/7",
    link: linkTo("/kontakt")
  }

  return (
    <section id="forside" className="relative min-h-screen flex flex-col justify-between overflow-hidden bg-[#FAF8F5]">
      {/* Background Video */}
      <div className="absolute inset-0 w-full h-full z-0 bg-[#FAF8F5]">
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          webkit-playsinline="true"
          controls={false}
          disablePictureInPicture
          disableRemotePlayback
          className="absolute inset-0 h-full w-full object-cover object-center pointer-events-none"
        >
          <source src={videoUrl} type="video/mp4" />
        </video>
        {/* Beige Overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-b md:bg-gradient-to-r from-[#FAF8F5]/80 via-[#FAF8F5]/65 md:via-[#FAF8F5]/75 to-[#FAF8F5]/45 md:to-[#FAF8F5]/20 md:from-[#FAF8F5]/95" />
        {/* Bottom fade to solid beige for seamless section transition */}
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#FAF8F5] to-transparent pointer-events-none" />
      </div>

      {/* Content Wrapper */}
      <div className="relative z-10 flex-1 flex flex-col justify-center max-w-7xl w-full mx-auto px-6 md:px-12 pt-28 pb-16 md:pt-36 md:pb-12 min-h-screen">
        {/* Top Spacer */}
        <div className="hidden md:block" />

        {/* Middle: Static Hero Content */}
        <div className="max-w-3xl animate-slideUp my-auto w-full">
          {/* Top Indicator */}
          <span className="font-mono text-xs sm:text-sm tracking-[0.2em] uppercase text-[#a78458] font-normal block mb-4 md:mb-6 leading-relaxed">
            {heroData.indicator}
          </span>

          {/* Main Headline */}
          <h1 
            className="font-marcellus text-left text-xl xs:text-2xl sm:text-4xl md:text-5xl font-light text-[#2C302E] leading-[1.2] md:leading-[1.15] mb-4 md:mb-6 uppercase break-words"
          >
            {language === 'da' ? (
              <>
                Erhvervsrengøring og <br /> Byggepladsservice <br />
                med <span className="font-medium text-[#7a5d35]">fokus på kvalitet</span> og stabilitet
              </>
            ) : (
              <>
                Commercial Cleaning and <br /> Construction Site Service <br />
                with <span className="font-medium text-[#7a5d35]">focus on quality</span> and stability
              </>
            )}
          </h1>

          {/* Description */}
          <p className="text-sm sm:text-lg font-semibold sm:font-normal text-[#2C302E]/80 leading-relaxed mb-8 md:mb-10 max-w-2xl">
            {heroData.description}
          </p>

          {/* Call to Actions */}
          <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
            {/* Primary CTA */}
            <button
              onClick={() => navigate(heroData.link)}
              className="group flex items-center justify-center gap-2 rounded-none bg-[#a78458] px-6 py-4 md:px-8 md:py-4.5 text-xs font-semibold tracking-widest text-white uppercase transition-all duration-300 hover:bg-[#917047] hover:-translate-y-1 hover:scale-[1.02]"
            >
              <span>{heroData.primaryCta}</span>
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>

            {/* Secondary CTA */}
            <a
              href="tel:53843361"
              className="flex items-center justify-center gap-2.5 rounded-none border border-[#2C302E]/30 bg-transparent px-6 py-4 md:px-8 md:py-4.5 text-xs font-semibold tracking-widest text-[#2C302E] uppercase transition-all duration-300 hover:border-[#2C302E] hover:bg-[#2C302E]/5 hover:-translate-y-1 hover:scale-[1.02]"
            >
              <Phone className="h-4 w-4 text-[#2C302E]" />
              <span>{heroData.secondaryCta}</span>
            </a>
          </div>

          {/* Key Selling Points / Counters (Moved closer) */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 pt-6 md:pt-8 border-t border-[#2C302E]/10 mt-8 md:mt-12">
            <div className="grid grid-cols-3 gap-4 sm:gap-12 w-full max-w-lg">
              <div>
                <p className="text-xl sm:text-3xl font-marcellus font-light text-[#2C302E]">
                  <CountUp end={15} suffix="+" />
                </p>
                <p className="text-[9px] sm:text-[10px] font-medium tracking-wider text-[#2C302E]/60 uppercase mt-1">
                  {language === 'da' ? 'Års Erfaring' : 'Years Experience'}
                </p>
              </div>
              <div>
                <p className="text-xl sm:text-3xl font-marcellus font-light text-[#2C302E]">24/7</p>
                <p className="text-[9px] sm:text-[10px] font-medium tracking-wider text-[#2C302E]/60 uppercase mt-1">
                  {language === 'da' ? 'Døgndækning' : '24/7 Support'}
                </p>
              </div>
              <div>
                <p className="text-xl sm:text-3xl font-marcellus font-light text-[#2C302E]">100%</p>
                <p className="text-[9px] sm:text-[10px] font-medium tracking-wider text-[#2C302E]/60 uppercase mt-1">
                  {language === 'da' ? 'Landsdækkende' : 'Nationwide'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
